import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation
} from 'react-router-dom';
import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import TelemetryProvider from "services/telemetry-provider";
import TelemetryIntitializer from "services/TelemetryIntitializer";
import { createBrowserHistory } from "history";
import PageInvitations from './components/invitations/page';
import { MsalProvider } from '@azure/msal-react';
import { loginRequest, msalConfig } from './AuthConfig';
import {
  BrowserAuthError,
  InteractionRequiredAuthError,
  PublicClientApplication
} from '@azure/msal-browser';
import RootLayout from 'layout';
import Header from 'components/ui/header';
import PageSearch from './components/search/page';
import PageProfile from './components/profile/profile';
import Footer from 'components/ui/footer';
import PageCourseDetails from 'components/course/page';
import PageSubscribe from 'components/subscribe/page';
import { LanguageProvider, useLanguage } from 'contexts/LanguageContext';
import { registerIcons, setIconOptions } from '@fluentui/react/lib/Styling';
import { ChevronDownIcon } from '@fluentui/react-icons-mdl2';
import { initializeIcons } from '@fluentui/react/lib/Icons';
import { SubscriptionProvider } from './contexts/SubscriptionContext'; // Import the SubscriptionProvider
import TagManager from 'react-gtm-module';

initializeIcons();

// Initialize GTM
const gtmId = process.env.REACT_APP_GTM_ID;
if (gtmId) {
  TagManager.initialize({
    gtmId: gtmId,
  });
}

// Suppress icon warnings.
setIconOptions({
  disableWarnings: true
});

registerIcons({
  icons: {
    ChevronDown: <ChevronDownIcon />
  }
});

const TrackPageView = () => {
  const location = useLocation();

  useEffect(() => {
    // Push page data to dataLayer without custom event
    // This will work with GTM's built-in "All Pages" trigger
    TagManager.dataLayer({
      dataLayer: {
        page_path: location.pathname,
        page_title: document.title,
        page_location: window.location.href,
      },
    });
    
    console.log('GTM: Updated dataLayer with page data for:', location.pathname);
  }, [location]);

  return null;
};

const pca = new PublicClientApplication(msalConfig);

const AppContent: React.FC = () => {
  const { lang } = useLanguage();
  const instrumentationKey = process.env.REACT_APP_APP_INSTRUMENTATION_KEY ?? "";
  const browserHistory = createBrowserHistory();
  let appInsights: ApplicationInsights | null = null;

  const signIn = async (msalInstance: PublicClientApplication) => {
    await msalInstance.initialize();
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      // There is at least one account in the cache, so try to sign in the user silently
      const account = msalInstance.getActiveAccount() || accounts[0]
      const silentRequest = {
        scopes: loginRequest.scopes,
        account: account,
        extraQueryParameters: {
          ui_locales: lang === 'fr' ? "fr-ca" : 'en-ca' // Change this to the desired locale
        },
        redirectUri: "/blank.html"
      };

      msalInstance.acquireTokenSilent(silentRequest)
        .then((response) => {
          // The user was successfully signed in silently
          console.log("User was signed in silently", response);
        })
        .catch((error) => {
          if (error instanceof InteractionRequiredAuthError) {
            // This error occurs when the user needs to be redirected to sign in. This could be the case when the user needs to consent to the scopes, or their password has expired.
            console.log("Interaction required for account", account);
            msalInstance.loginRedirect({
              scopes: loginRequest.scopes,
              extraQueryParameters: {
                ui_locales: lang === 'fr' ? "fr-ca" : 'en-ca' // Change this to the desired locale
              }
            });
          } else if (error instanceof BrowserAuthError && error.errorCode === "monitor_window_timeout") {
            // Handle the timeout error specifically
            console.log("Silent token acquisition failed due to timeout, redirecting to login", error);
            msalInstance.loginRedirect({
              scopes: loginRequest.scopes,
              loginHint: account.username,
              extraQueryParameters: {
                ui_locales: lang === 'fr' ? "fr-ca" : 'en-ca' // Change this to the desired locale
              },
              prompt: 'account_select'
            });
          } else {
            console.trace(error);
          }
        });
    }
  }

  useEffect(() => {
    signIn(pca);
  }, [lang]);

  return (
    <RootLayout>
      <MsalProvider instance={pca}>
        <SubscriptionProvider> {/* Wrap your application with SubscriptionProvider */}
          <Router>
            <TrackPageView />
            <TelemetryProvider
              history={browserHistory}
              enabled
              instrumentationKey={instrumentationKey}
              after={() => {
                appInsights = TelemetryIntitializer.getAppInsights();
                console.log("appInsights: ", appInsights);
              }}
            >
              <Header />
              <main className="grow">
                <Routes>
                  {/* Page d'invitation */}
                  {[':lang/invitations/:invitation',
                    'invitations/:invitation'].map((path) => (
                      <Route key={path} path={path} element={
                        <PageInvitations />
                      } />
                    ))
                  }

                  {/* Page abonnez-vous */}
                  {[':fr/abonnement',
                    ':en/subscribe',
                    'abonnement',
                    'subscribe'].map((path) => (
                      <Route key={path} path={path} element={
                        <PageSubscribe />
                      } />
                    ))
                  }

                  {/* Page recherche formations */}
                  {[':fr/recherche',
                    ':en/search',
                    'recherche',
                    'search'].map((path) => (
                      <Route key={path} path={path} element={<PageSearch />} />
                    ))
                  }

                  {/* Page de session de formation */}
                  {[':en/courses/:courseid',
                    ':fr/sessions/:courseid',
                    'courses/:courseid',
                    'sessions/:courseid'].map((path) => (
                      <Route key={path} path={path} element={<PageCourseDetails />} />
                    ))
                  }
                  
                  {/* Page présences */}
                  {[':fr/historique',
                    ':en/history',
                    'historique',
                    'history'].map((path) => (
                      <Route key={path} path={path} element={<PageProfile />} />
                    ))
                  }

                  <Route key=':lang' path=':lang' element={<PageSearch />} />
                  <Route key='/' path='/' element={<PageSearch />} />
                  {/* Add other routes here */}
                </Routes>
              </main>
              <Footer />
            </TelemetryProvider>
          </Router>
        </SubscriptionProvider>
      </MsalProvider>
    </RootLayout>
  );
};

const App: React.FC = () => {
  return (
    <LanguageProvider>
      <AppContent />
    </LanguageProvider>
  );
};

export default App;
